import {ElementRef, Injectable} from '@angular/core';
import {EmbedService} from "./embed.service";
import {PortalService} from "./portal.service";


/**
 * Generic service for working/scrolling with/to html elements.
 */
@Injectable({
  providedIn: 'root'
})
export class ElementService {

  /**
   * The timeout that is used for scrolling to elements to wait for their creation.
   */
  public static readonly SCROLLTIMEOUT = 100;

  /**
   * constructor
   * @param embedService
   * @param portalService
   */
  constructor(public embedService: EmbedService, public portalService: PortalService) {}

  /**
   * Generic function to scroll to a html element with some offset.
   * @param element
   * @param extraOffset
   */
  public static scrollToTarget(element: HTMLElement, extraOffset: number = 0): void {
    // add some small timeout in case the element must be shown before.
    setTimeout(() => {
      const offsetPosition = ElementService.getTopOffset(element) + extraOffset;

      // only do the scrolling, if the element is not visible.
      if(window.scrollY > offsetPosition || Math.abs(window.scrollY + window.innerHeight) < offsetPosition) {
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }, ElementService.SCROLLTIMEOUT);
  }

  /**
   * returns the top offset position of an element.
   * @param element
   * @param headerOffset
   */
  public static getTopOffset(element: HTMLElement, headerOffset: number = 50): number {
    if(!element) {
      // avoind race condition where objects are already unset
      return 0;
    }
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    return elementPosition - headerOffset;
  }

  /**
   * Scrolls to a given HTML Element.
   * @param element
   * @param extraOffset
   */
  public scrollToElement(element: HTMLElement, extraOffset: number = 0): void {
    if(this.embedService.isEmbedded()) {
      window.setTimeout(() => {
        const stickyHeader = this.portalService.getConfig('embed.stickyheader.mobile');
        const offset = (stickyHeader ? parseFloat(stickyHeader) : 0) + extraOffset;
        this.embedService.scrollToTop(ElementService.getTopOffset(element) - offset);
      }, ElementService.SCROLLTIMEOUT);
    }
    else {
      ElementService.scrollToTarget(element, extraOffset);
    }
  }

  /**
   * Scrolls to a given offset.
   * @param offset
   */
  public scrollToOffset(offset: number): void {
    if(this.embedService.isEmbedded()) {
      this.embedService.scroll(offset);
    }
    else {
      window.scrollTo({
        top: window.scrollY + offset,
        behavior: "smooth"
      });
    }
  }

  /**
   * init the toolbars.
   * @param element
   */
  public initTooltips(element: ElementRef): void {setTimeout(() => {
    // counter to set dynamic id's / aria attributes on tooltips
    let tooltipCounter = 1;

    for(const node of element.nativeElement.querySelectorAll('[data-tooltip]')) {
      // click on target-element to open tooltip-element
      node.addEventListener('click', event => this.positionTooltips(event.target));
      // open tooltip with keydown enter or space
      node.addEventListener('keydown', event => {
        if (event.key === "Enter" || event.key === '') {
          this.positionTooltips(event.target);
        }
      });

      // set attributes to make tooltip accessible
      node.setAttribute("aria-describedby", "tooltip-" + tooltipCounter);
      node.setAttribute("tabindex", 0);

      //click event to close tooltips
      window.addEventListener('click', (event) => {
        for(const node of element.nativeElement.querySelectorAll('[data-tooltip]')) {
          // check if we clicked on a tooltip-element
          if(node !== event.target && event.target !== node.firstElementChild) {
            node.classList.remove("active");
          }
        }
        // close tooltip when button is triggered
        for (const closeEl of element.nativeElement.querySelectorAll('button')) {
          if(closeEl === event.target && event.target) {
            node.classList.remove("active");
          }
        }
      });

      // keydown event to close tooltips
      window.addEventListener('keydown', (event: KeyboardEvent) => {
        for(const node of element.nativeElement.querySelectorAll('[data-tooltip]')) {
          // close tooltip with escape button
          if(event.key === "Escape") {
            node.classList.remove("active");
          }
        }
      });

      // close tooltip automatically if there is no more keyboard focus on it
      window.addEventListener('focusout', (event) => {
        if (!node.contains(event.relatedTarget)) {
          node.classList.remove("active");
        }
      });

      const content = document.createElement("span");
      content.setAttribute("id", "tooltip-" + tooltipCounter++);
      content.setAttribute("role", "tooltip");
      content.innerText = node.getAttribute("data-tooltip");

      // insert closing icon to active tooltip
      const iconX = document.createElement("i");
      iconX.classList.add("icon-x");
      content.prepend(iconX);
      node.appendChild(content);

      // wrap closing icon into button to ensure accessibility
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("aria-label", "schliessen");
      button.appendChild(iconX);
      content.prepend(button);
    }

    //add event listener for window resize
    window.addEventListener('resize', () => {
      const activeElement = element.nativeElement.querySelector('[data-tooltip].active');
      if(activeElement) {
        this.positionTooltips(activeElement);
      }
    });
  }, 1000);}

  /**
   * position the tooltips
   */
  public positionTooltips(node: HTMLElement): void {
    const boundings = node.getBoundingClientRect();
    const visibleWidth = document.body?.clientWidth?? window.outerWidth;
    node.classList?.add("active");
    node.classList?.remove("right", "left", "middle", "small");
    //check if target-element is on the right side of the screen
    if((boundings.right / visibleWidth > 0.66 && boundings.x / visibleWidth > 0.35) || (boundings.right / visibleWidth > 0.66 && boundings.height > 25 && visibleWidth >= 640) && node.classList?.add) {
      node.classList?.add("right");
    }
    //check if target-element is on the left side of the screen
    else if((boundings.x / visibleWidth <= 0.65 && visibleWidth >= 640) || (boundings.x / visibleWidth < 0.5 && boundings.height > 25 && visibleWidth >= 640) && node.classList?.add) {
      node.classList?.add("left");
    }
    //check if target-element is on small screen and on two lines
    else if((visibleWidth < 640 && boundings.height > 25)) {
      node.classList?.add("middle", "iOS");
    }
    //check if target-element is on small screen and on one line only
    else if((visibleWidth < 640 && boundings.height < 40)) {
      node.classList?.add("small");
    }
  }

  /**
   * set focus on element for accessibility
   * @param element
   */
  public setFocusOnElement(element: ElementRef): void {
    element?.nativeElement?.focus();
  }
}
